import { SerializedEditorState, SerializedLexicalNode } from "lexical";
import { useCallback, useState, useEffect, useMemo } from "react";
import { singletonHook } from "react-singleton-hook";
import { AttachmentType } from "@web-src/modules/chats/types";
import { ChatMessagePayloadConference } from "@jugl-web/rest-api";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";

export interface ChatInputState {
  body?: SerializedEditorState<SerializedLexicalNode>;
  bodyRaw?: string;
  attachments?: { file: File; caption: string }[];
  attachmentType?: AttachmentType;
  voiceRecordingFile?: File;
  conference?: ChatMessagePayloadConference;
  updatedAt?: number;
}

interface ChatInputStateStorage {
  [key: string]: {
    [key: string]: {
      temp?: ChatInputState;
      permanent?: ChatInputState;
    };
  };
}

interface UseChatInputStateType {
  chatsState: Record<string, ChatInputState>;
  chatsTempState: Record<string, ChatInputState>;
  isInitialized: boolean;
  updateChatState: (
    chatId: string,
    state: Exclude<ChatInputState, "updatedAt">,
    params?: { merge?: boolean; temp?: boolean }
  ) => void;
}

const STORAGE_KEY = "chat_input_state:v2";

const useChatInputState = (): UseChatInputStateType => {
  const { entityId } = useEntityProvider();
  const [chatsState, setChatsState] = useState<ChatInputStateStorage>(() => {
    // Initialize state from localStorage if available
    if (typeof window !== "undefined") {
      const storedState = localStorage.getItem(STORAGE_KEY);
      return storedState ? JSON.parse(storedState) : {};
    }
    return {};
  });

  // Update localStorage whenever chatsState changes
  useEffect(() => {
    if (typeof window !== "undefined" && entityId) {
      // Only store the body, not the attachments
      const stateToStore = JSON.parse(JSON.stringify(chatsState));
      stateToStore[entityId] = Object.entries(
        chatsState[entityId] || {}
      ).reduce(
        (acc, [key, value]) => {
          if (!acc[key]) {
            acc[key] = {};
          }
          acc[key].permanent = {
            body: value.permanent?.body,
            bodyRaw: value.permanent?.bodyRaw,
            conference: value.permanent?.conference,
            updatedAt: value.permanent?.updatedAt,
          };
          delete acc[key].temp;
          return acc;
        },
        {} as ChatInputStateStorage[string]
      );

      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToStore));
    }
  }, [chatsState, entityId]);

  const updateChatState: UseChatInputStateType["updateChatState"] = useCallback(
    (chatId, state, { merge, temp } = { merge: true, temp: false }) => {
      if (!entityId) {
        return;
      }
      setChatsState((prev) => ({
        ...prev,
        [entityId]: {
          ...prev[entityId],
          [chatId]: {
            temp: temp
              ? merge
                ? {
                    ...prev[entityId]?.[chatId]?.temp,
                    ...state,
                    updatedAt: +new Date(),
                  }
                : state
              : prev[entityId]?.[chatId]?.temp,
            permanent: !temp
              ? merge
                ? {
                    ...prev[entityId]?.[chatId]?.permanent,
                    ...state,
                    updatedAt: +new Date(),
                  }
                : state
              : prev[entityId]?.[chatId]?.permanent,
          },
        },
      }));
    },
    [entityId]
  );

  const tempEntityChatsState = useMemo(() => {
    if (!entityId) {
      return {};
    }
    const result: Record<string, ChatInputState> = {};
    Object.entries(chatsState[entityId] || {}).forEach(([chatId, value]) => {
      if (value.temp) {
        result[chatId] = value.temp;
      }
    });
    return result;
  }, [chatsState, entityId]);

  const permanentEntityChatsState = useMemo(() => {
    if (!entityId) {
      return {};
    }
    const result: Record<string, ChatInputState> = {};
    Object.entries(chatsState[entityId] || {}).forEach(([chatId, value]) => {
      if (value.permanent) {
        result[chatId] = value.permanent;
      }
    });
    return result;
  }, [chatsState, entityId]);

  return {
    chatsState: permanentEntityChatsState,
    chatsTempState: tempEntityChatsState,
    updateChatState,
    isInitialized: true,
  };
};

export default singletonHook(
  {
    chatsState: {},
    chatsTempState: {},
    updateChatState: () => null,
    isInitialized: false,
  },
  useChatInputState
);
